/**
 * cockpitAccess — fail-closed Zugriffslogik + stabile Formular-Route für das
 * Owner-Cockpit (`architecture-cockpit-route-split.md` §5.1).
 *
 * DREI entkoppelte Bausteine (§3 der Architektur, „3 Entkopplungen"):
 *   - `UX_COCKPIT_ENABLED`: globales GA-Flag (später-Flip). Fail-closed.
 *   - `COCKPIT_BETA_UIDS` / `canUseCockpit(uid)`: WER an `/dashboard` das
 *     Cockpit statt des Rechnungsformulars sieht — pro Beta-UID JETZT (env-
 *     getrieben) und global per Flag später. Rein RENDER-Zeit-Entscheidung
 *     (uid-abhängig), damit sie unabhängig von der Formular-Route variieren
 *     kann (behebt den früheren Self-Loop, §1).
 *   - `INVOICE_FORM_ROUTE`: eine ECHTE, flag-freie Konstante — das Ziel JEDES
 *     „Rechnung erstellen"-Verweises. Sie zeigt konstant auf die immer
 *     registrierte Formular-Route, nie auf `/dashboard` ⇒ Self-Loop
 *     strukturell unmöglich (§3 E2).
 *
 * **Warum Env-Var-Beta-Allowlist statt hartkodiertem Array** (Muster
 * `bankReconcileAccess.ts`): `.env.production` führt die zwei Beta-UIDs bereits
 * env-getrieben für den Bank-Abgleich — gleiche Aktivierungs-Mechanik, kein
 * UID-Hardcode im Code, byte-gleiche Parse-Logik.
 *
 * **Bewusst OHNE Tier-Term** (anders als `canUseBankReconcile`): das Cockpit ist
 * die künftige Startseite für ALLE, kein Ultra-Differenzierer — bei GA-Flip
 * soll jeder Nutzer es sehen.
 *
 * Firebase-frei (nur `process.env`) — importierbar aus `routes/*RoutePaths.ts`
 * und `config/navigation.ts`, die selbst KEIN Firebase ziehen dürfen (Muster
 * `config/unifiedInboxAccess.ts`/`bankReconcileAccess.ts`).
 *
 * @module config/cockpitAccess
 */

/**
 * Allgemeines GA-Feature-Flag für das Owner-Cockpit. Fail-closed: nur bei
 * explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load
 * ausgewertet (Muster `UNIFIED_INBOX_ENABLED`). Bei GA-Flip sieht JEDER Nutzer
 * das Cockpit an `/dashboard`.
 */
export const UX_COCKPIT_ENABLED: boolean =
  process.env.REACT_APP_UX_COCKPIT_ENABLED === 'true';

/**
 * Beta-Erprobung Owner-Cockpit — ENV-VAR-getrieben (Muster
 * `bankReconcileAccess.ts`), NICHT hartkodiert. Komma-separierte UIDs, getrimmt,
 * leere Einträge raus. Leer, solange die Env-Var nicht gesetzt ist
 * (fail-closed).
 */
export const COCKPIT_BETA_UIDS: readonly string[] = (
  process.env.REACT_APP_UX_COCKPIT_BETA_UIDS ?? ''
)
  .split(',')
  .map((uid) => uid.trim())
  .filter((uid) => uid.length > 0);

/**
 * Entscheidet fail-closed, ob ein Nutzer das Owner-Cockpit an `/dashboard`
 * sieht. Rein uid-abhängig (kein Tier) — Beta-Mitgliedschaft hängt an der
 * eingeloggten Identität.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt bzw. während der Auth-Hydration).
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das
 *          allgemeine GA-Flag aktiv ist. Sonst `false` (auch bei null/undefined
 *          — fail-closed, kein Crash).
 */
export function canUseCockpit(uid: string | null | undefined): boolean {
  if (uid && COCKPIT_BETA_UIDS.includes(uid)) {
    return true;
  }
  return UX_COCKPIT_ENABLED;
}

/**
 * Ziel-Route für JEDEN „Rechnung erstellen"-Verweis in der App (Nav-Actions,
 * G5-Empty-CTAs, Onboarding-Wizard-Close, AppTour, InvoiceTypeSwitch,
 * Checklisten-Deep-Link). ECHTE Konstante, flag-frei: die Formular-Route
 * (`/dashboard/rechnungen/neu`) ist immer registriert und rendert immer das
 * `InvoiceDashboard` (§3 E1/E2). Kein Consumer zeigt je auf `/dashboard`
 * (das ist der Index-Entscheid) ⇒ Self-Loop strukturell unmöglich.
 */
export const INVOICE_FORM_ROUTE = '/dashboard/rechnungen/neu' as const;
